import React from 'react';
import { Market } from '../hooks/usePredictionMarket';

interface MarketCycleStatusProps {
  market:     Market;
  /** Show a compact one-line badge instead of the full card */
  compact?:   boolean;
}

type CyclePhase = 'active' | 'expiring-soon' | 'expired' | 'resolved-yes' | 'resolved-no';

const PHASE_CONFIG: Record<CyclePhase, { label: string; badgeClass: string; icon: string }> = {
  'active':         { label: 'Active',          badgeClass: 'badge-open',    icon: '🟢' },
  'expiring-soon':  { label: 'Expiring Soon',   badgeClass: 'badge-expired', icon: '🟡' },
  'expired':        { label: 'Expired – Awaiting Settlement', badgeClass: 'badge-expired', icon: '🔴' },
  'resolved-yes':   { label: 'Resolved: YES ✓', badgeClass: 'badge-yes',     icon: '✅' },
  'resolved-no':    { label: 'Resolved: NO ✗',  badgeClass: 'badge-no',      icon: '❌' },
};

const SECONDS_PER_DAY = 86_400;
/** Markets expiring within this many seconds are flagged as "expiring soon". */
const EXPIRING_SOON_THRESHOLD = SECONDS_PER_DAY; // 24 hours

function getPhase(market: Market): CyclePhase {
  if (market.resolved) {
    return market.outcome ? 'resolved-yes' : 'resolved-no';
  }

  const now       = Date.now() / 1000;
  const timeLeft  = market.deadline - now;

  if (timeLeft <= 0) return 'expired';
  if (timeLeft < EXPIRING_SOON_THRESHOLD) return 'expiring-soon';
  return 'active';
}

function formatCountdown(deadline: number): string {
  const seconds = deadline - Math.floor(Date.now() / 1000);
  if (seconds <= 0) return 'Expired';

  const d = Math.floor(seconds / 86400);
  const h = Math.floor((seconds % 86400) / 3600);
  const m = Math.floor((seconds % 3600) / 60);

  if (d > 0) return `${d}d ${h}h remaining`;
  if (h > 0) return `${h}h ${m}m remaining`;
  return `${m}m remaining`;
}

/**
 * MarketCycleStatus
 *
 * Visual indicator of a market's current lifecycle phase.
 * Renders a badge (compact mode) or a status card with countdown timer.
 */
export default function MarketCycleStatus({ market, compact }: MarketCycleStatusProps) {
  const phase  = getPhase(market);
  const config = PHASE_CONFIG[phase];

  if (compact) {
    return (
      <span className={`badge ${config.badgeClass}`} title={config.label}>
        {config.icon} {config.label}
      </span>
    );
  }

  return (
    <div className={`market-cycle-status phase-${phase}`}>
      <div className="cycle-phase-indicator">
        <span className="cycle-icon">{config.icon}</span>
        <span className={`badge ${config.badgeClass}`}>{config.label}</span>
      </div>

      {!market.resolved && market.deadline > 0 && (
        <div className="cycle-countdown">
          {formatCountdown(market.deadline)}
        </div>
      )}

      {/* Phase progress bar (visual only) */}
      <div className="cycle-progress-bar">
        {phase === 'active' && (
          <div className="cycle-progress-fill cycle-active" style={{ width: '33%' }} />
        )}
        {phase === 'expiring-soon' && (
          <div className="cycle-progress-fill cycle-expiring" style={{ width: '66%' }} />
        )}
        {(phase === 'expired' || phase.startsWith('resolved')) && (
          <div className="cycle-progress-fill cycle-done" style={{ width: '100%' }} />
        )}
      </div>

      {market.deadline > 0 && (
        <div className="cycle-deadline">
          Deadline: {new Date(market.deadline * 1000).toLocaleString()}
        </div>
      )}
    </div>
  );
}
